import React from 'react';
import { Widget, WidgetConfig } from '../Widget/Widget';
import { cn } from '../../../lib/utils';
import { renderIcon } from './iconUtils';
import { ExtendedMarkdown } from '../../../components/Pleri/components/MessageContent/ExtendedMarkdown';
import { FilterProvider, FilterBar } from '../Filter';
import { Filter } from '../Filter/types';
// Simple widget config with grid positioning
export interface SimpleWidgetConfig extends Omit<WidgetConfig, 'layout'> {
  cols?: number; // How many columns to span (1, 2, 3, etc.)
  rows?: number; // How many rows to span (1, 2, 3, etc.)
  x?: number; // Starting column position (1, 2, 3, etc.)
  y?: number; // Starting row position (1, 2, 3, etc.)
}

// Simple section config
export interface SimpleSectionConfig {
  id?: string;
  title?: string;
  titleIcon?: string; // Lucide icon name for the section title
  description?: string;
  sectionDescription?: string; // Markdown content for section description (between subtitle and widgets)
  sectionFooter?: string; // Markdown content for section footer
  className?: string;
  widgets: SimpleWidgetConfig[];
  cols?: number; // Number of columns in this section's grid (default: 3)
}

// Simplified dashboard config
export interface SimpleDashboardConfig {
  title?: string;
  titleIcon?: string; // Lucide icon name for the dashboard title
  description?: string;
  sections: SimpleSectionConfig[];
  filters?: Filter[]; // Dashboard-level filters
}

export interface DashboardProps {
  config: SimpleDashboardConfig;
  className?: string;
  isStreaming?: boolean;
}

// Auto-grid component using CSS Grid
const AutoGrid: React.FC<{
  section: SimpleSectionConfig;
  isStreaming?: boolean;
}> = ({ section, isStreaming = false }) => {
  const { widgets, cols = 3 } = section;

  // Calculate grid dimensions based on widget positions
  const maxX = Math.max(
    ...(widgets?.map((w) => (w.x || 1) + (w.cols || 1) - 1) ?? []),
    cols,
  );
  const maxY = Math.max(
    ...(widgets?.map((w) => (w.y || 1) + (w.rows || 1) - 1) ?? []),
    1,
  );

  // Create grid template - use auto for rows to allow natural expansion
  const gridTemplateColumns = `repeat(${maxX}, 1fr)`;
  const gridTemplateRows = `repeat(${maxY}, auto)`;

  return (
    <div
      className="grid w-full gap-4"
      style={{
        gridTemplateColumns,
        gridTemplateRows,
      }}
    >
      {widgets?.map((widget, index) => (
        <div
          key={`widget-${index}`}
          className="h-full min-h-[200px] w-full"
          style={{
            gridColumn: `${widget.x || 1} / span ${widget.cols || 1}`,
            gridRow: `${widget.y || 1} / span ${widget.rows || 1}`,
          }}
        >
          <Widget
            config={widget}
            className="h-full"
            isStreaming={isStreaming}
          />
        </div>
      ))}
    </div>
  );
};

// Section component
const Section: React.FC<{
  section: SimpleSectionConfig;
  isStreaming?: boolean;
}> = ({ section, isStreaming = false }) => {
  return (
    <div className={section.className || ''}>
      {/* Section Header */}
      {(section.title || section.description) && (
        <div className="mb-6">
          {section.title && (
            <h2 className="mb-2 flex items-center gap-2 text-2xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              {section.titleIcon && (
                <span>{renderIcon({ name: section.titleIcon, size: 24 })}</span>
              )}
              {section.title}
            </h2>
          )}
          {section.description && (
            <p className="text-base leading-relaxed font-normal text-gray-600 dark:text-gray-400">
              {section.description}
            </p>
          )}
        </div>
      )}

      {/* Section Description */}
      {section.sectionDescription && (
        <div className="mb-6">
          <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-400">
            <ExtendedMarkdown content={section.sectionDescription} />
          </div>
        </div>
      )}

      {/* Auto-grid */}
      <AutoGrid section={section} isStreaming={isStreaming} />

      {/* Section Footer */}
      {section.sectionFooter && (
        <div className="mt-6">
          <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-400">
            <ExtendedMarkdown content={section.sectionFooter} />
          </div>
        </div>
      )}
    </div>
  );
};

export const Dashboard: React.FC<DashboardProps> = ({
  config,
  className,
  isStreaming = false,
}) => {
  return (
    <FilterProvider initialFilters={config.filters || []}>
      <div
        className={cn(
          'bg-gray-50/30 p-6 font-sans antialiased dark:bg-gray-800/30',
          className,
        )}
      >
        {/* Dashboard Header */}
        {(config.title || config.description) && (
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {config.title && (
                  <h1 className="mb-2 flex items-center gap-3 text-4xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    {config.titleIcon && (
                      <span>
                        {renderIcon({ name: config.titleIcon, size: 32 })}
                      </span>
                    )}
                    {config.title}
                  </h1>
                )}
                {config.description && (
                  <p className="text-lg leading-relaxed font-normal text-gray-600 dark:text-gray-400">
                    {config.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {config.filters && config.filters.length > 0 && (
          <div className="mb-8">
            <FilterBar
              filters={config.filters}
              showClearAll={true}
              collapsible={true}
              defaultCollapsed={false}
            />
          </div>
        )}

        {/* Dashboard Sections */}
        <div className="space-y-10">
          {config.sections?.map((section, index) => (
            <Section
              key={section.id || index}
              section={section}
              isStreaming={isStreaming}
            />
          ))}
        </div>
      </div>
    </FilterProvider>
  );
};
